<script lang="ts">
	import { ButtonV2, Loader, Text } from '@Shelly/shelly-design-system';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { format } from 'svelte-i18n';
	import FlowAnimation from './FlowAnimation.svelte';
	import AppUpdateRequired from './AppUpdateRequired.svelte';
	import AISceneModalWrapper from './AISceneModalWrapper.svelte';
	import { checkAgentHealth } from '../../services/agent-health';

	let compatibilityCheckStarted = false;
	let isCompatibilityCheckLoading = false;

	$: isAppVersionSupported =
		$aiSceneChatStore.agentHealth[$aiSceneChatStore.activeAgentId]?.isAppVersionSupported !== false;

	$: if ($aiSceneChatStore.showOnboardingModal && !compatibilityCheckStarted) {
		compatibilityCheckStarted = true;
		checkAppCompatibility();
	} else if (!$aiSceneChatStore.showOnboardingModal && compatibilityCheckStarted) {
		resetCompatibilityCheck();
	}

	function resetCompatibilityCheck() {
		compatibilityCheckStarted = false;
		isCompatibilityCheckLoading = false;
	}

	async function checkAppCompatibility() {
		isCompatibilityCheckLoading = true;
		const agentId = $aiSceneChatStore.activeAgentId;
		const health = await checkAgentHealth(agentId);
		aiSceneChatStore.setAgentHealth(agentId, health);
		isCompatibilityCheckLoading = false;
	}

	function handleClose() {
		aiSceneChatStore.dismissOnboarding();
	}

	function handleStart() {
		aiSceneChatStore.acceptOnboarding();
	}

	function handleTermsClick(e: MouseEvent) {
		e.preventDefault();
		aiSceneChatStore.openPrivacyPolicy();
	}
</script>

<AISceneModalWrapper
	open={$aiSceneChatStore.showOnboardingModal}
	onClose={handleClose}
	bordered
	size="content"
>
	{#if isCompatibilityCheckLoading}
		<div class="compatibility-loading">
			<Loader size="l" />
			<Text styleType="body" class="text-color-muted">
				{$format('ai_checking_app_version', { default: 'Checking app version...' })}
			</Text>
		</div>
	{:else if !isAppVersionSupported}
		<AppUpdateRequired />
	{:else}
		<div class="modal-content">
			<div class="animation-wrapper">
				<FlowAnimation />
			</div>
			<h1 class="modal-title">
				{$format('ai_onboarding_title', { default: 'Welcome to Shelly Assistant' })}
			</h1>

			<div class="features-grid">
				<div class="feature-card">
					<div class="feature-icon">
						<img src="./images/ai/ai-onboarding-search.svg" alt="" width="44" height="44" />
					</div>
					<p class="feature-text">
						{$format('ai_onboarding_feature_info', {
							default: 'Get detailed information for your home',
						})}
					</p>
				</div>
				<div class="feature-card">
					<div class="feature-icon">
						<img src="./images/ai/ai-onboarding-gears.svg" alt="" width="44" height="44" />
					</div>
					<p class="feature-text">
						{$format('ai_onboarding_feature_scenes', {
							default: 'Create scenes for advanced automation',
						})}
					</p>
				</div>
				<div class="feature-card">
					<div class="feature-icon">
						<img
							src="./images/ai/ai-onboarding-lightbulb.svg"
							alt=""
							width="44"
							height="44"
						/>
					</div>
					<p class="feature-text">
						{$format('ai_onboarding_feature_learn', {
							default: 'Learn more about the capabilities of your home',
						})}
					</p>
				</div>
			</div>

			<p class="disclaimer">
				{$format('ai_onboarding_disclaimer_part1', {
					default: 'By clicking "Start using Shelly Assistant" button you agree to ',
				})}
				<a
					href="/terms-and-conditions"
					class="terms-link"
					on:click|preventDefault={handleTermsClick}
				>
					{$format('ai_onboarding_terms_link', {
						default: "Shelly Assistant's terms of use",
					})}
				</a>.
				<br />
				{$format('ai_onboarding_disclaimer_part2', {
					default:
						'Shelly Assistant is an experimental feature. Use as guidance only. Confirm important details before acting.',
				})}
			</p>

			<div class="start-btn-wrap">
				<ButtonV2 variant="primary" size="m" on:click={handleStart}>
					{$format('ai_onboarding_cta', { default: 'Start using Shelly Assistant' })}
				</ButtonV2>
			</div>
		</div>
	{/if}
</AISceneModalWrapper>

<style>
	.compatibility-loading {
		display: flex;
		flex: 1;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-m);
	}

	.modal-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		flex: 1;
		gap: 32px;
		padding: 0 32px;
		position: relative;
		z-index: 1;
		overflow-y: auto;
	}

	.animation-wrapper {
		width: 140px;
		height: 153px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		mix-blend-mode: screen;
	}

	.animation-wrapper :global(canvas) {
		width: 140px;
		height: 140px;
	}

	.modal-title {
		font-family: Inter, sans-serif;
		font-size: 22px;
		font-weight: 600;
		line-height: 27px;
		color: var(--color-text-primary, #eaeaea);
		text-align: center;
		margin: 0;
		flex-shrink: 0;
	}

	.features-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 10px;
		width: 100%;
		max-width: 573px;
		flex-shrink: 0;
	}

	.feature-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		overflow: visible;
	}

	.feature-icon {
		width: 44px;
		height: 44px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.feature-icon img {
		width: 44px;
		height: 44px;
		display: block;
	}

	.feature-text {
		font-family: Inter, sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-text-primary, #eaeaea);
		text-align: center;
		margin: 0;
	}

	.disclaimer {
		font-family: Inter, sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: 16px;
		color: #a1a2a2;
		text-align: center;
		margin: 0;
		max-width: 573px;
	}

	.terms-link {
		color: inherit;
		text-decoration: underline;
		cursor: pointer;
	}

	.terms-link:hover {
		opacity: 0.9;
	}

	.start-btn-wrap {
		text-align: center;
		flex-shrink: 0;
	}

	@media (max-width: 768px) {
		.modal-content {
			padding: 0 32px;
			gap: 32px;
		}

		.animation-wrapper {
			width: 140px;
			height: 153px;
		}

		.animation-wrapper :global(canvas) {
			width: 140px;
			height: 140px;
		}

		.modal-title {
			font-size: 22px;
			line-height: 27px;
		}

		.features-grid {
			display: flex;
			flex-direction: column;
			gap: 10px;
			width: 100%;
			max-width: 100%;
			padding: 0;
			align-items: stretch;
		}

		.feature-card {
			flex-direction: row;
			justify-content: flex-start;
			align-items: flex-start;
			gap: 16px;
		}

		.feature-icon {
			flex-shrink: 0;
		}

		.feature-text {
			flex: 1;
			text-align: left;
			min-width: 0;
		}

		.disclaimer {
			padding: 0 24px;
			max-width: 100%;
		}

		.start-btn-wrap {
			width: 100%;
			max-width: none;
			align-self: stretch;
		}

		.start-btn-wrap :global(button) {
			width: 100%;
		}
	}
</style>
